import React, { useMemo } from 'react';
import { Box, Typography, Paper, Stack, Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Chrono } from 'react-chrono';
import 'react-chrono/dist/style.css';
import { TimelineEvent } from '../../types/timeline';

interface TimelinePlotViewProps {
  events: TimelineEvent[];
  sortOrder: 'asc' | 'desc';
  onToggleSortOrder: () => void;
}

type ChronoMode = 'VERTICAL' | 'VERTICAL_ALTERNATING' | 'HORIZONTAL';

// Helper functions for formatting
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const formatLocation = (location: { city?: string; state?: string; country?: string }): string => {
  const parts = [location.city, location.state, location.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '';
};

/**
 * TimelinePlotView - Visual timeline plot display
 *
 * Displays events in a visual timeline format with horizontal or vertical layouts.
 * Uses react-chrono for the visual timeline rendering.
 */
export const TimelinePlotView: React.FC<TimelinePlotViewProps> = ({
  events,
  sortOrder,
  onToggleSortOrder,
}) => {
  const [mode, setMode] = React.useState<ChronoMode>('VERTICAL_ALTERNATING');

  // Sort events by date
  const sortedEvents = useMemo(() => {
    const sorted = [...events].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return sortOrder === 'asc'
        ? a.date.localeCompare(b.date)
        : b.date.localeCompare(a.date);
    });
    return sorted;
  }, [events, sortOrder]);

  // Transform events to react-chrono format
  const chronoItems = useMemo(() => {
    return sortedEvents.map((event) => ({
      title: event.date ? formatDate(event.date) : 'Unknown Date',
      cardTitle: event.personName,
      cardSubtitle: event.type.charAt(0).toUpperCase() + event.type.slice(1),
      cardDetailedText: event.description,
    }));
  }, [sortedEvents]);

  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: ChronoMode | null) => {
    if (newMode !== null) {
      setMode(newMode);
    }
  };

  if (events.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={400}
        p={4}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Events to Display
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters to see events in the timeline
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            Layout:
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            size="small"
          >
            <ToggleButton value="VERTICAL" aria-label="vertical">
              Vertical
            </ToggleButton>
            <ToggleButton value="VERTICAL_ALTERNATING" aria-label="alternating">
              Alternating
            </ToggleButton>
            <ToggleButton value="HORIZONTAL" aria-label="horizontal">
              Horizontal
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flexGrow: 1 }} />

          <Chip
            label={`${events.length} ${events.length === 1 ? 'Event' : 'Events'}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Stack>
      </Paper>

      {/* Timeline */}
      <Paper sx={{ p: 3 }}>
        <Chrono
          items={chronoItems}
          mode={mode}
          cardHeight={150}
          scrollable={mode === 'HORIZONTAL' ? { scrollbar: true } : false}
          theme={{
            primary: '#1976d2',
            secondary: '#f5f5f5',
            cardBgColor: '#ffffff',
            titleColor: '#000000',
            titleColorActive: '#1976d2',
          }}
          fontSizes={{
            cardSubtitle: '0.85rem',
            cardText: '0.875rem',
            cardTitle: '1rem',
            title: '0.9rem',
          }}
        />
      </Paper>

      {/* Event Details Table */}
      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Event Details
        </Typography>
        <Stack spacing={1}>
          {sortedEvents.map((event, index) => (
            <Box
              key={event.id}
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: index % 2 === 0 ? 'grey.50' : 'white',
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {event.date ? formatDate(event.date) : 'Unknown'}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  <Chip
                    label={event.type}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {event.personName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {event.description}
                  </Typography>
                  {event.location && (
                    <Typography variant="caption" color="text.secondary">
                      📍 {formatLocation(event.location)}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};
